import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMachine } from '@xstate/react'

import interviewMachine from '../components/interviewMachine'
import LoadingPage from '../components/loadingPage'
import PreFormInterview from '../components/preInterviewComponents/preFormInterview'
import PreTextInterview from '../components/preInterviewComponents/preTextInterview'
import PreVideoInterview from '../components/preInterviewComponents/preVideoInterview'
import FormInterview from './formInterview'
import TextInterview from './textInterview'
import VideoInterview from './videoInterview'
import MobileVideoInterview from './mobileVideoInterview'
import useDeviceType from '../hooks/useDeviceType'
import apiCalendar from '../services/api/calendar'
import apiInterviewVersions from '../services/api/interviewVersion'
import { getRawJWTFromToken } from '../utilities/tokenUtils'
import NotificationBoxPage from '../components/notificationBoxPage'
import PreInPersonInterview from '../components/preInterviewComponents/preInPersonInterview'
import InPersonInterview from './inPersonInterview'

const Switch = ({ render, children }) =>
  children.find((child) => child.props.value === render)

const Case = ({ children }) => children

export default function InterviewStageController() {
  const { t } = useTranslation()
  const { interviewToken } = useParams()
  const rawJWT = getRawJWTFromToken(interviewToken)
  const deviceType = useDeviceType()

  const [state, send] = useMachine(interviewMachine, {
    services: {
      fetchInterview: () =>
        apiInterviewVersions.startInterview(rawJWT).then((res) => res),
      fetchInterviewAfterExpiration: () =>
        apiCalendar.getDataWithoutVerification(rawJWT).then((data) => data),
    },
  })
  const [showCounter, setShowCounter] = useState(true)

  const rescheduleInterview = () => {
    apiCalendar
      .resetInterview(rawJWT)
      .then((res) => {
        window.location.href = `/calendar/${res.updatedToken}`
      })
      .catch(() => {
        send('LOAD_FAILURE')
      })
  }

  if (
    !state?.context?.interviewTime &&
    state?.context?.interviewType === 'INPERSON' &&
    !state?.context?.started
  ) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message="Waiting for Candidate to book an interview"
        rescheduleInterview={rescheduleInterview}
        rescheduleEnabled={false}
        isFinished={state?.context?.finished}
        isExpired={state.context?.errorMsg === 'expired token'}
      />
    )
  }

  // Commented for testing purpose
  // if (
  //   isAndroid &&
  //   (state.value === 'videoInterview' || state.value === 'preVideoInterview')
  // ) {
  //   return (
  //     <AndroidDeviceNotification
  //       rescheduleInterview={rescheduleInterview}
  //       rescheduleEnabled={state.context?.rescheduleEnabled}
  //       isFinished={state?.context?.finished}
  //     />
  //   )
  // }

  return (
    <Switch render={state.value}>
      <Case value="loading">
        <LoadingPage />
      </Case>
      <Case value="preInPersonInterview">
        <PreInPersonInterview
          rescheduleInterview={rescheduleInterview}
          rescheduleEnabled={state.context.rescheduleEnabled}
          isFinished={state?.context?.finished}
          interviewType={state.context.interviewType}
          interviewTime={state.context.interviewTime}
          setShowCounter={setShowCounter}
          showCounter={showCounter}
        />
      </Case>
      <Case value="preFormInterview">
        <PreFormInterview
          interviewTime={state.context.interviewTime}
          setShowCounter={setShowCounter}
          showCounter={showCounter}
        />
      </Case>
      <Case value="preTextInterview">
        <PreTextInterview
          rescheduleInterview={rescheduleInterview}
          rescheduleEnabled={state.context.rescheduleEnabled}
          isFinished={state?.context?.finished}
          interviewType={state.context.interviewType}
          interviewTime={state.context.interviewTime}
          setShowCounter={setShowCounter}
          showCounter={showCounter}
        />
      </Case>
      <Case value="preVideoInterview">
        <PreVideoInterview
          rescheduleInterview={rescheduleInterview}
          rescheduleEnabled={state.context.rescheduleEnabled}
          isFinished={state?.context?.finished}
          interviewType={state.context.interviewType}
          interviewTime={state.context.interviewTime}
          setShowCounter={setShowCounter}
          showCounter={showCounter}
        />
      </Case>
      <Case value="inPersonInterview">
        <InPersonInterview />
      </Case>
      <Case value="formInterview">
        <FormInterview />
      </Case>
      <Case value="textInterview">
        <TextInterview />
      </Case>
      <Case value="videoInterview">
        {deviceType !== 'desktop' ? (
          <MobileVideoInterview />
        ) : (
          <VideoInterview />
        )}
      </Case>
      <Case value="success">
        <NotificationBoxPage
          messageType="success"
          message={t('pages.thankYouMsg')}
        />
      </Case>
      <Case value="end">
        <NotificationBoxPage
          messageType="error"
          message={t('pages.interviewEnded')}
        />
      </Case>
      <Case value="error">
        <NotificationBoxPage
          messageType="error"
          message={t('pages.linkExpired')}
          rescheduleInterview={rescheduleInterview}
          rescheduleEnabled={state.context?.rescheduleEnabled}
          isFinished={state?.context?.finished}
          isExpired={
            state.context?.errorMsg?.toLowerCase().includes('expired') ||
            state.context?.errorMsg?.toLowerCase().includes('unauthorized')
          }
        />
      </Case>
    </Switch>
  )
}
